import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Trash2, Pencil, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const PART_STATUS_LABELS = {
  needed: "Needed", ordered: "Ordered", shipped: "Shipped",
  delivered: "Delivered", picked_up: "Picked up",
};

export const PART_STATUS_STYLES = {
  needed: "bg-red-50 text-red-700 border-red-200",
  ordered: "bg-blue-50 text-blue-700 border-blue-200",
  shipped: "bg-amber-50 text-amber-800 border-amber-300",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-300",
  picked_up: "bg-emerald-50 text-emerald-800 border-emerald-300",
};

export const trackingUrl = (tn) => {
  const t = (tn || "").replace(/\s+/g, "");
  if (!t) return null;
  if (/^1Z/i.test(t)) return `https://www.ups.com/track?tracknum=${t}`;
  if (/^9\d{19,21}$/.test(t)) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${t}`;
  if (/^\d{12,15}$/.test(t)) return `https://www.fedex.com/fedextrack/?trknbr=${t}`;
  return `https://www.google.com/search?q=${encodeURIComponent(t + " tracking")}`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

const emptyPart = { name: "", part_number: "", source: "local", company: "", cost: "", order_number: "", tracking_number: "", eta: "" };

const PartRow = ({ p, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/parts/${p.id}`, body);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Part removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setEdit({
      company: p.company || "", cost: p.cost ?? "",
      order_number: p.order_number || "", tracking_number: p.tracking_number || "", eta: p.eta || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    await patch({ ...edit, cost: edit.cost === "" ? null : Number(edit.cost), eta: edit.eta || null });
    setEditing(false);
    toast.success("Part updated");
  };

  const tUrl = trackingUrl(p.tracking_number);
  const nextActions = p.source === "local"
    ? (p.status === "needed" ? [["picked_up", "Mark picked up"]] : [])
    : { needed: [["ordered", "Mark ordered"]], ordered: [["shipped", "Mark shipped"], ["delivered", "Mark delivered"]], shipped: [["delivered", "Mark delivered"]] }[p.status] || [];

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1.5" data-testid={`part-row-${p.id}`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-900 truncate">{p.name}</span>
        {p.part_number && <span className="text-sm font-mono text-slate-600">{p.part_number}</span>}
        <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0 capitalize">{p.source}</Badge>
        <Badge variant="outline" className={`${PART_STATUS_STYLES[p.status] || ""} rounded-full text-[10px] px-2 py-0 ml-auto`} data-testid={`part-status-${p.id}`}>
          {PART_STATUS_LABELS[p.status] || p.status}
        </Badge>
      </div>
      <div className="text-xs text-slate-600 space-x-2">
        {p.company && <span>From <strong>{p.company}</strong></span>}
        {typeof p.cost === "number" && <span>· ${p.cost.toFixed(2)}</span>}
        {p.ordered_by_name && <span>· {p.source === "local" ? "Picked up" : "Ordered"} by {p.ordered_by_name}{p.ordered_at ? ` on ${fmtDate(p.ordered_at)}` : ""}</span>}
      </div>
      {(p.order_number || p.tracking_number || p.eta) && (
        <div className="text-xs text-slate-600 space-x-2">
          {p.order_number && <span>Order #{p.order_number}</span>}
          {p.tracking_number && tUrl && (
            <a href={tUrl} target="_blank" rel="noreferrer" className="text-blue-700 underline inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              {p.tracking_number} <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {p.eta && <span>· ETA {p.eta}</span>}
        </div>
      )}
      {editing ? (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Input placeholder="Company" value={edit.company} onChange={(e) => setEdit((s) => ({ ...s, company: e.target.value }))} className="h-8 text-xs" />
          <Input placeholder="Cost $" type="number" value={edit.cost} onChange={(e) => setEdit((s) => ({ ...s, cost: e.target.value }))} className="h-8 text-xs" />
          <Input placeholder="Order #" value={edit.order_number} onChange={(e) => setEdit((s) => ({ ...s, order_number: e.target.value }))} className="h-8 text-xs" />
          <Input placeholder="Tracking #" value={edit.tracking_number} onChange={(e) => setEdit((s) => ({ ...s, tracking_number: e.target.value }))} className="h-8 text-xs" />
          <Input type="date" value={edit.eta} onChange={(e) => setEdit((s) => ({ ...s, eta: e.target.value }))} className="h-8 text-xs" />
          <div className="flex gap-1">
            <Button type="button" size="sm" className="h-8 bg-blue-700 hover:bg-blue-800 text-xs flex-1" onClick={saveEdit} disabled={busy} data-testid={`part-save-${p.id}`}>Save</Button>
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {nextActions.map(([st, label]) => (
            <Button type="button" key={st} size="sm" variant="outline" className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" disabled={busy}
              onClick={() => patch({ status: st })} data-testid={`part-action-${st}-${p.id}`}>
              {label}
            </Button>
          ))}
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-slate-500 ml-auto" onClick={startEdit} disabled={busy} data-testid={`part-edit-${p.id}`}>
            <Pencil className="w-3 h-3" />
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={remove} disabled={busy} data-testid={`part-delete-${p.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export const PartsPanel = ({ ticketId }) => {
  const [parts, setParts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyPart);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}/parts`);
      setParts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) {
      toast.error("Part name is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tickets/${ticketId}/parts`, {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        eta: form.eta || null,
      });
      toast.success("Part added");
      setForm(emptyPart);
      setAdding(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="parts-panel">
      {parts.length === 0 && !adding && (
        <p className="text-sm text-slate-500">No parts tracked for this job yet.</p>
      )}
      {parts.map((p) => <PartRow key={p.id} p={p} onChanged={load} />)}
      {adding ? (
        <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-2" data-testid="part-add-form">
          <Input placeholder="Part name * (e.g. Drain pump)" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="part-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Part #" value={form.part_number} onChange={(e) => setForm((s) => ({ ...s, part_number: e.target.value }))} data-testid="part-number" />
            <Select value={form.source} onValueChange={(v) => setForm((s) => ({ ...s, source: v }))}>
              <SelectTrigger data-testid="part-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local (pick up)</SelectItem>
                <SelectItem value="online">Online (order)</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Company / store" value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} data-testid="part-company" />
            <Input placeholder="Cost $" type="number" value={form.cost} onChange={(e) => setForm((s) => ({ ...s, cost: e.target.value }))} data-testid="part-cost" />
          </div>
          {form.source === "online" && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Order # (optional)" value={form.order_number} onChange={(e) => setForm((s) => ({ ...s, order_number: e.target.value }))} data-testid="part-order-number" />
              <Input placeholder="Tracking # (optional)" value={form.tracking_number} onChange={(e) => setForm((s) => ({ ...s, tracking_number: e.target.value }))} data-testid="part-tracking" />
              <Input type="date" value={form.eta} onChange={(e) => setForm((s) => ({ ...s, eta: e.target.value }))} data-testid="part-eta" title="Expected arrival" />
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" onClick={add} disabled={busy} className="bg-blue-700 hover:bg-blue-800 flex-1" data-testid="part-add-save">Add part</Button>
            <Button type="button" variant="outline" onClick={() => { setAdding(false); setForm(emptyPart); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={() => setAdding(true)} className="w-full gap-2 border-dashed" data-testid="part-add-btn">
          <Plus className="w-4 h-4" /> Add part
        </Button>
      )}
    </div>
  );
};

export const PartsIcon = Package;
